<script lang="ts">
  /**
   * Власний відеоплеєр: <video> з HLS-потоком Bunny і наша панель керування.
   *
   * Замінив iframe-плеєр Bunny, бо той у WebView Telegram на iPhone не вміє
   * одночасно AirPlay з картинкою і відновлення позиції. На пристроях Apple
   * потік грає нативно (саме це дає картинку в AirPlay), на решті — через
   * hls.js, який підвантажується лише там.
   *
   * Про матеріали й події плеєр нічого не знає: сторінка передає, звідки
   * почати (`startAt`), і отримує прогрес через `onProgress`.
   */
  import { untrack } from 'svelte';
  import type Hls from 'hls.js';
  import PlayerControls from './PlayerControls.svelte';
  import { getTelegramWebApp } from '../../lib/telegram';
  import { PLAYER_DEFAULTS, type PlayerSettings } from '../../lib/player/settings';
  import {
    canSetVolume,
    fullscreenMode,
    rotatesInFullscreen,
    isAppleWebKit,
    supportsAirPlay,
    supportsPip,
    type WebKitVideo,
  } from '../../lib/player/capabilities';
  import type { PlayerActions, PlayerCaps, PlayerState } from '../../lib/player/types';

  interface Props {
    hlsUrl: string;
    posterUrl?: string | null;
    /** Секунда, з якої продовжити; 0 — з початку. */
    startAt?: number;
    title?: string;
    settings?: PlayerSettings;
    /** Пульс відтворення (не частіше разу на 5 с). Без нього трекінг вимкнено (адмін-прев'ю). */
    onProgress?: (sec: number, duration: number) => void;
  }
  let {
    hlsUrl,
    posterUrl = null,
    startAt = 0,
    title = '',
    settings = PLAYER_DEFAULTS,
    onProgress,
  }: Props = $props();

  const THROTTLE_SEC = 5; // сервер чекає не більше одного запису прогресу на 5 с
  const RESUME_TOLERANCE_SEC = 3; // ближче до точки відновлення — вважаємо, що дійшли
  const RESUME_GRACE_MS = 10_000; // далі людина там, де сама захотіла, — звітуємо
  const QUALITY_KEY = 'mf:video-quality';
  const RATE_KEY = 'mf:video-rate';

  interface Variant {
    height: number;
    url: string;
  }

  let videoEl = $state<HTMLVideoElement | undefined>(undefined);
  let wrapEl = $state<HTMLDivElement | undefined>(undefined);
  let reloadKey = $state(0);
  // Плеєр лежить шаром на весь екран (Telegram на телефоні або запасний варіант).
  let overlay = $state(false);
  let caps = $state<PlayerCaps>({ airplay: false, pip: false, volume: false });

  const ps = $state<PlayerState>({
    paused: true,
    started: false,
    waiting: false,
    currentTime: 0,
    duration: 0,
    buffered: 0,
    muted: false,
    volume: 1,
    rate: 1,
    quality: 0,
    qualities: [],
    airplayAvailable: false,
    airplayActive: false,
    pipActive: false,
    fullscreen: false,
    rotated: false,
    failed: false,
  });

  let hls: Hls | null = null;
  let variants: Variant[] = [];
  // Куди стати після завантаження метаданих: точка відновлення на старті або
  // поточна позиція, коли джерело міняється на іншу якість.
  let pendingSeek = 0;
  let resumeAfterSwitch = false;
  // Поки відтворення не дійшло до точки відновлення, прогрес не шлемо: позиція
  // біля нуля затерла б збережену.
  let awaitingResume: number | null = null;
  let firstUpdateAt = 0;
  let lastSent = -Infinity;

  function stored(key: string): number {
    try {
      return Number(localStorage.getItem(key)) || 0;
    } catch {
      return 0;
    }
  }
  function store(key: string, value: number): void {
    if (!settings.rememberChoices) return;
    try {
      localStorage.setItem(key, String(value));
    } catch {
      // приватний режим — вибір просто не запам'ятається
    }
  }

  /** Якість, якої в цього відео немає, зводиться до авто. */
  function validQuality(height: number): number {
    return variants.some((v) => v.height === height) ? height : 0;
  }
  function variantUrl(height: number): string {
    return variants.find((v) => v.height === height)?.url ?? hlsUrl;
  }

  /** Перелік якостей для нативного шляху (hls.js повідомляє свої рівні сам). */
  async function loadVariants(): Promise<void> {
    try {
      const lines = (await (await fetch(hlsUrl)).text()).split('\n').map((l) => l.trim());
      const found: Variant[] = [];
      for (let i = 0; i < lines.length; i++) {
        const m = /^#EXT-X-STREAM-INF:.*RESOLUTION=\d+x(\d+)/.exec(lines[i]);
        const uri = lines[i + 1];
        if (m && uri && !uri.startsWith('#')) {
          found.push({ height: Number(m[1]), url: new URL(uri, hlsUrl).href });
        }
      }
      variants = found.sort((a, b) => b.height - a.height);
    } catch {
      variants = []; // без переліку відео грає в авто, меню якості не показується
    }
  }

  // ── Джерело, події <video>, прогрес ───────────────────────────────────
  $effect(() => {
    const v = videoEl as WebKitVideo | undefined;
    if (!v) return;
    void reloadKey; // «Спробувати ще» перезапускає ефект
    const src = hlsUrl;
    const resumeFrom = startAt > 0 ? startAt : 0;
    const prefs = untrack(() => ({
      quality: (settings.rememberChoices && stored(QUALITY_KEY)) || settings.defaultQuality,
      rate: (settings.rememberChoices && stored(RATE_KEY)) || 1,
      speeds: settings.speeds,
    }));
    let cancelled = false;

    v.setAttribute('x-webkit-airplay', 'allow');
    caps = { airplay: supportsAirPlay(v), pip: supportsPip(v), volume: canSetVolume() };
    pendingSeek = resumeFrom;
    awaitingResume = resumeFrom > 0 ? resumeFrom : null;
    firstUpdateAt = 0;
    ps.failed = false;
    ps.rate = prefs.speeds.includes(prefs.rate) ? prefs.rate : 1;
    // До першого Play показуємо позицію, з якої відео продовжиться.
    ps.currentTime = resumeFrom;

    const report = (force: boolean): void => {
      if (!onProgress || !Number.isFinite(v.duration) || v.duration <= 0) return;
      const sec = v.currentTime;
      if (awaitingResume !== null) {
        firstUpdateAt ||= Date.now();
        const pending =
          sec < awaitingResume - RESUME_TOLERANCE_SEC && Date.now() - firstUpdateAt < RESUME_GRACE_MS;
        if (pending) return;
        awaitingResume = null;
      }
      if (!force && Math.abs(sec - lastSent) < THROTTLE_SEC) return;
      lastSent = sec;
      onProgress(sec, v.duration);
    };
    const syncBuffered = (): void => {
      for (let i = 0; i < v.buffered.length; i++) {
        if (v.buffered.start(i) <= v.currentTime + 0.5 && v.buffered.end(i) >= v.currentTime) {
          ps.buffered = v.buffered.end(i);
          return;
        }
      }
    };

    const handlers: Record<string, (e: Event) => void> = {
      loadedmetadata: () => {
        if (Number.isFinite(v.duration)) ps.duration = v.duration;
        if (pendingSeek > 0) v.currentTime = pendingSeek;
        pendingSeek = 0;
        v.playbackRate = ps.rate; // зміна джерела скидає швидкість
        if (resumeAfterSwitch) {
          resumeAfterSwitch = false;
          void v.play().catch(() => {});
        }
      },
      durationchange: () => {
        if (Number.isFinite(v.duration)) ps.duration = v.duration;
      },
      timeupdate: () => {
        // Перед стрибком на точку відновлення <video> ще на нулі — не блимаємо ним.
        if (pendingSeek === 0) ps.currentTime = v.currentTime;
        syncBuffered();
        report(false);
      },
      progress: syncBuffered,
      play: () => {
        ps.paused = false;
        ps.started = true;
      },
      pause: () => {
        ps.paused = true;
        report(true);
      },
      playing: () => (ps.waiting = false),
      canplay: () => (ps.waiting = false),
      seeked: () => (ps.waiting = false),
      waiting: () => (ps.waiting = true),
      seeking: () => (ps.waiting = true),
      ended: () => {
        ps.paused = true;
        if (onProgress && Number.isFinite(v.duration) && v.duration > 0) onProgress(v.duration, v.duration);
      },
      volumechange: () => {
        ps.muted = v.muted;
        ps.volume = v.volume;
      },
      ratechange: () => (ps.rate = v.playbackRate),
      error: () => {
        if (!hls) ps.failed = true;
      },
      enterpictureinpicture: () => (ps.pipActive = true),
      leavepictureinpicture: () => (ps.pipActive = false),
      webkitpresentationmodechanged: () => (ps.pipActive = v.webkitPresentationMode === 'picture-in-picture'),
      webkitplaybacktargetavailabilitychanged: (e) => {
        ps.airplayAvailable = (e as Event & { availability?: string }).availability === 'available';
      },
      webkitcurrentplaybacktargetiswirelesschanged: () => {
        ps.airplayActive = v.webkitCurrentPlaybackTargetIsWireless ?? false;
      },
    };
    for (const [name, fn] of Object.entries(handlers)) v.addEventListener(name, fn);

    if (isAppleWebKit(v)) {
      void loadVariants().then(() => {
        if (cancelled) return;
        ps.qualities = variants.map((x) => x.height);
        ps.quality = validQuality(prefs.quality);
        v.src = ps.quality ? variantUrl(ps.quality) : src;
      });
    } else {
      void import('hls.js').then(({ default: HlsCtor }) => {
        if (cancelled) return;
        if (!HlsCtor.isSupported()) {
          v.src = src; // без MSE лишається те, що браузер зробить із посиланням сам
          return;
        }
        // Воркер потребував би `worker-src blob:` у CSP nginx.
        const h = new HlsCtor({ enableWorker: false, startPosition: pendingSeek > 0 ? pendingSeek : -1 });
        hls = h;
        pendingSeek = 0;
        h.on(HlsCtor.Events.MANIFEST_PARSED, (_e, data) => {
          variants = data.levels.map((l) => ({ height: l.height, url: '' })).sort((a, b) => b.height - a.height);
          ps.qualities = variants.map((x) => x.height);
          ps.quality = validQuality(prefs.quality);
          applyHlsQuality();
        });
        h.on(HlsCtor.Events.ERROR, (_e, data) => {
          if (!data.fatal) return;
          if (data.type === HlsCtor.ErrorTypes.NETWORK_ERROR) h.startLoad();
          else if (data.type === HlsCtor.ErrorTypes.MEDIA_ERROR) h.recoverMediaError();
          else ps.failed = true;
        });
        h.loadSource(src);
        h.attachMedia(v);
      });
    }

    return () => {
      cancelled = true;
      report(true);
      for (const [name, fn] of Object.entries(handlers)) v.removeEventListener(name, fn);
      hls?.destroy();
      hls = null;
    };
  });

  function applyHlsQuality(): void {
    if (!hls) return;
    hls.currentLevel = ps.quality ? hls.levels.findIndex((l) => l.height === ps.quality) : -1;
  }

  // ── Повний екран ───────────────────────────────────────────────────────
  let enteredTgFs = false;

  function syncViewport(): void {
    if (!wrapEl || !videoEl) return;
    wrapEl.style.setProperty('--fs-w', `${window.innerWidth}px`);
    wrapEl.style.setProperty('--fs-h', `${window.innerHeight}px`);
    ps.rotated = rotatesInFullscreen() && window.innerHeight > window.innerWidth;
  }

  function enterOverlay(): void {
    syncViewport();
    overlay = true;
    ps.fullscreen = true;
  }

  function enterFullscreen(): void {
    const v = videoEl as WebKitVideo | undefined;
    if (!v || !wrapEl) return;
    const mode = fullscreenMode(v);
    if (mode === 'telegram') {
      enterOverlay();
      const tg = getTelegramWebApp();
      if (tg?.requestFullscreen && tg.isVersionAtLeast?.('8.0') && !tg.isFullscreen) {
        enteredTgFs = true;
        tg.requestFullscreen();
      }
    } else if (mode === 'native') {
      // Буває вимкнений політикою вбудовування (WebView десктопного Telegram) — тоді шар.
      wrapEl.requestFullscreen().catch(enterOverlay);
    } else if (mode === 'ios-video') {
      try {
        v.webkitEnterFullscreen?.();
      } catch {
        // iOS не розгортає відео, поки не завантажені метадані (до першого
        // Play). Запускаємо відтворення й розгортаємо, щойно вони прийдуть.
        v.addEventListener(
          'loadedmetadata',
          () => {
            try {
              v.webkitEnterFullscreen?.();
            } catch {
              // не вийшло — лишаємось у вбудованому плеєрі, відео вже грає
            }
          },
          { once: true },
        );
        void v.play().catch(() => {});
      }
    } else {
      enterOverlay();
    }
  }

  function exitFullscreen(): void {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    if (overlay) {
      overlay = false;
      ps.fullscreen = false;
      ps.rotated = false;
      const tg = getTelegramWebApp();
      // Скасовуємо лише своє: бот міг запустити застосунок одразу на весь екран.
      if (enteredTgFs && tg?.exitFullscreen && tg.isFullscreen) tg.exitFullscreen();
      enteredTgFs = false;
    }
  }

  $effect(() => {
    const onNativeChange = (): void => {
      if (!overlay) ps.fullscreen = document.fullscreenElement === wrapEl;
    };
    document.addEventListener('fullscreenchange', onNativeChange);
    return () => document.removeEventListener('fullscreenchange', onNativeChange);
  });

  $effect(() => {
    if (!overlay) return;
    const tg = getTelegramWebApp();
    const onTgChange = (): void => {
      syncViewport();
      // Вийшли з повного екрана кнопкою самого Telegram.
      if (enteredTgFs && tg && !tg.isFullscreen) exitFullscreen();
    };
    const onTgFailed = (): void => {
      enteredTgFs = false; // шар і без цього займає весь доступний екран
    };
    window.addEventListener('resize', syncViewport);
    tg?.onEvent?.('fullscreenChanged', onTgChange);
    tg?.onEvent?.('fullscreenFailed', onTgFailed);
    return () => {
      window.removeEventListener('resize', syncViewport);
      tg?.offEvent?.('fullscreenChanged', onTgChange);
      tg?.offEvent?.('fullscreenFailed', onTgFailed);
      // Сторінку закрили з розгорнутим плеєром — повертаємо Telegram як було.
      if (enteredTgFs && tg?.exitFullscreen && tg.isFullscreen) tg.exitFullscreen();
      enteredTgFs = false;
    };
  });

  // ── Дії панелі ─────────────────────────────────────────────────────────
  function seek(sec: number): void {
    const v = videoEl;
    if (!v) return;
    const target = Math.max(0, Math.min(ps.duration || sec, sec));
    ps.currentTime = target;
    // До метаданих перемотати не можна — запам'ятовуємо, стане після завантаження.
    if (v.readyState >= 1) v.currentTime = target;
    else pendingSeek = target;
    // Людина сама вибрала місце — точка відновлення більше не діє.
    awaitingResume = null;
  }

  const actions: PlayerActions = {
    togglePlay: () => {
      const v = videoEl;
      if (!v) return;
      if (v.paused) {
        ps.waiting = true;
        void v.play().catch(() => (ps.waiting = false));
      } else {
        v.pause();
      }
    },
    seek,
    skip: (delta) => seek((videoEl?.readyState ? videoEl.currentTime : ps.currentTime) + delta),
    toggleMute: () => {
      if (videoEl) videoEl.muted = !videoEl.muted;
    },
    setVolume: (volume) => {
      if (!videoEl) return;
      videoEl.volume = volume;
      videoEl.muted = volume === 0;
    },
    setRate: (rate) => {
      if (videoEl) videoEl.playbackRate = rate;
      ps.rate = rate;
      store(RATE_KEY, rate);
    },
    setQuality: (height) => {
      const v = videoEl;
      if (!v || height === ps.quality) return;
      ps.quality = height;
      store(QUALITY_KEY, height);
      if (hls) {
        applyHlsQuality();
        return;
      }
      // Нативно якість — це інший плейлист: міняємо джерело й вертаємось на місце.
      pendingSeek = v.currentTime;
      resumeAfterSwitch = !v.paused;
      v.src = height ? variantUrl(height) : hlsUrl;
    },
    airplay: () => (videoEl as WebKitVideo | undefined)?.webkitShowPlaybackTargetPicker?.(),
    togglePip: () => {
      const v = videoEl as WebKitVideo | undefined;
      if (!v) return;
      if (document.pictureInPictureEnabled) {
        if (document.pictureInPictureElement) void document.exitPictureInPicture().catch(() => {});
        else void v.requestPictureInPicture().catch(() => {});
      } else {
        v.webkitSetPresentationMode?.(v.webkitPresentationMode === 'picture-in-picture' ? 'inline' : 'picture-in-picture');
      }
    },
    toggleFullscreen: () => (ps.fullscreen ? exitFullscreen() : enterFullscreen()),
    retry: () => reloadKey++,
  };

  function onKey(e: KeyboardEvent): void {
    const v = videoEl;
    if (!v) return;
    switch (e.key) {
      case ' ':
      case 'k':
        actions.togglePlay();
        break;
      case 'ArrowLeft':
        actions.skip(-settings.skipSeconds);
        break;
      case 'ArrowRight':
        actions.skip(settings.skipSeconds);
        break;
      case 'ArrowUp':
        actions.setVolume(Math.min(1, v.volume + 0.1));
        break;
      case 'ArrowDown':
        actions.setVolume(Math.max(0, v.volume - 0.1));
        break;
      case 'f':
        actions.toggleFullscreen();
        break;
      case 'm':
        actions.toggleMute();
        break;
      case 'Escape':
        if (!overlay) return;
        exitFullscreen();
        break;
      default:
        return;
    }
    e.preventDefault();
  }
</script>

<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
<div
  class="player"
  class:fs={overlay}
  class:rot={overlay && ps.rotated}
  bind:this={wrapEl}
  style:--player-accent={settings.accentColor ?? 'var(--pu)'}
  role="region"
  aria-label={title || 'Відеоплеєр'}
  tabindex="0"
  onkeydown={onKey}
>
  <div class="stage">
    <!-- svelte-ignore a11y_media_has_caption -->
    <video bind:this={videoEl} playsinline preload="metadata" poster={posterUrl}></video>
    <PlayerControls state={ps} {actions} {caps} {settings} />
  </div>
</div>

<style>
  .player {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    overflow: hidden;
    background: black;
    margin-bottom: 18px;
    outline: none;
  }
  .stage {
    position: relative;
    width: 100%;
    height: 100%;
  }
  video {
    width: 100%;
    height: 100%;
    display: block;
    background: black;
  }
  /* Звичайний Fullscreen API (комп'ютер, браузер). */
  .player:fullscreen {
    aspect-ratio: auto;
    border-radius: 0;
    margin: 0;
  }
  /* Шар на весь екран. Розміри дає JS (--fs-w/--fs-h): у WebView Telegram 100vh
     не дорівнює видимій висоті. `animation: none` знімає transform, який лишає
     на цьому елементі анімація появи .card-list, — він перекрив би поворот.
     Відступи тримають панель подалі від системних смуг і кнопок Telegram. */
  .player.fs {
    animation: none;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
    width: var(--fs-w);
    height: var(--fs-h);
    aspect-ratio: auto;
    border-radius: 0;
    margin: 0;
    box-sizing: border-box;
    --inset-top: calc(
      max(env(safe-area-inset-top), var(--tg-safe-area-inset-top, 0px)) +
        var(--tg-content-safe-area-inset-top, 0px)
    );
    --inset-right: max(env(safe-area-inset-right), var(--tg-safe-area-inset-right, 0px));
    --inset-bottom: max(env(safe-area-inset-bottom), var(--tg-safe-area-inset-bottom, 0px));
    --inset-left: max(env(safe-area-inset-left), var(--tg-safe-area-inset-left, 0px));
    padding: var(--inset-top) var(--inset-right) var(--inset-bottom) var(--inset-left);
  }
  /* Телефон тримають вертикально: кладемо плеєр на бік. Блок бере розміри
     екрана навпаки й обертається навколо лівого верхнього кута, тож верх
     екрана стає лівим краєм плеєра — відступи обертаються разом із ним. */
  .player.fs.rot {
    width: var(--fs-h);
    height: var(--fs-w);
    left: var(--fs-w);
    transform-origin: 0 0;
    transform: rotate(90deg);
    padding: var(--inset-right) var(--inset-bottom) var(--inset-left) var(--inset-top);
  }
</style>
